import { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AdvancedPagination,
  Button,
  Card,
  Divider,
  Input,
  RadixTable,
  TColumn,
} from '@features/ui';
import {
  getCouponBatches,
  deleteCouponBatch,
  getCouponCodeSuggestions,
} from '@src/services/coupons';
import { TCouponBatch } from '@src/types';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';
import { toast } from 'react-toastify';
import { CouponCodesModal } from '@features/admin/couponBatches/CouponCodesModal';
import { debounce } from 'lodash';

type TCouponCodeSuggestionItem = {
  code: string;
  batch_id: number;
  comment: string;
  label: string;
};

type TCouponCodeSuggestionsRes = {
  items: TCouponCodeSuggestionItem[];
  more_rows_found: number;
};

export const CouponsPage = () => {
  const { auth } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [batches, setBatches] = useState<TCouponBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 1,
  });

  // Modal state
  const [modalBatchId, setModalBatchId] = useState<number | null>(null);
  const [searchCode, setSearchCode] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<TCouponCodeSuggestionItem | null>(null);
  const [suggestions, setSuggestions] = useState<TCouponCodeSuggestionItem[]>(
    []
  );
  const [moreRowsFound, setMoreRowsFound] = useState(0);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const fetchBatches = useCallback(
    (
      page: number,
      pageSize: number,
      suggestion?: TCouponCodeSuggestionItem | null
    ) => {
      setLoading(true);
      const normalizedCode = (suggestion?.code ?? '').trim().toLowerCase();

      // Server-side search by code
      getCouponBatches({ page, size: pageSize, code: normalizedCode })
        .then((res) => {
          const data = res.data;
          const loadedBatches = data.batches || [];

          setBatches(loadedBatches);

          const totalPages = Math.max(
            1,
            Math.ceil((data.total || 0) / (data.size || 20))
          );

          setPagination({
            currentPage: data.page || 1,
            pageSize: data.size || 20,
            totalItems: data.total || 0,
            totalPages,
          });
        })
        .catch(() => {
          setBatches([]);
        })
        .finally(() => setLoading(false));
    },
    []
  );

  useEffect(() => {
    const codeFromParams = searchParams.get('code');

    if (codeFromParams) {
      const normalized = codeFromParams.trim();

      setSearchCode(normalized);
      setSelectedSuggestion({
        code: normalized,
        batch_id: 0,
        comment: '',
        label: normalized,
      });
    }

    // Do not fetch here; the effect below will fetch exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Single source of truth for loading batches.
  useEffect(() => {
    fetchBatches(1, 20, selectedSuggestion);
  }, [fetchBatches, selectedSuggestion]);

  const fetchSuggestions = useCallback(async (query: string) => {
    const q = query.trim();

    if (!q) {
      setSuggestions([]);
      setMoreRowsFound(0);
      setSuggestionsOpen(false);

      return;
    }

    setSuggestionsLoading(true);

    try {
      const res = await getCouponCodeSuggestions(q);
      const data = res.data as unknown as TCouponCodeSuggestionsRes;

      const items = Array.isArray(data.items) ? data.items : [];

      setSuggestions(items.slice(0, 10));
      setMoreRowsFound(Number(data.more_rows_found ?? 0));
      setSuggestionsOpen(
        items.length > 0 || Number(data.more_rows_found ?? 0) > 0
      );
    } catch {
      setSuggestions([]);
      setMoreRowsFound(0);
      setSuggestionsOpen(false);
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const debouncedFetchSuggestions = useMemo(
    () => debounce((q: string) => void fetchSuggestions(q), 400),
    [fetchSuggestions]
  );

  useEffect(() => {
    return () => {
      debouncedFetchSuggestions.cancel();
    };
  }, [debouncedFetchSuggestions]);

  // delete handler
  const handleDelete = useCallback(
    (id?: number) => {
      if (!id) return;
      const ok = window.confirm(
        'Delete this coupon batch? This action cannot be undone.'
      );

      if (!ok) return;

      deleteCouponBatch(id)
        .then(() => {
          // refresh list
          fetchBatches(pagination.currentPage, pagination.pageSize);
        })
        .catch(() => toast.error('Failed to delete coupon batch'));
    },
    [fetchBatches, pagination.currentPage, pagination.pageSize]
  );

  const handleEdit = useCallback(
    (id?: number) => {
      if (id) navigate(`/admin/coupons/${id}`);
      else navigate(`/admin/coupons/create`);
    },
    [navigate]
  );

  const getBatchStatus = useCallback((batch: TCouponBatch) => {
    if (batch.is_disabled) {
      return { label: 'Inactive', color: 'text-gray-400' };
    }

    const now = new Date();
    const endDate = batch.end_date ? new Date(batch.end_date) : null;

    if (endDate && now > endDate) {
      return { label: 'Expired', color: 'text-red-500' };
    }

    return { label: 'Active', color: 'text-green-500' };
  }, []);

  const handleOpenCouponByBatch = useCallback((batchId: number) => {
    setModalBatchId(batchId);
  }, []);

  const columns = useMemo(
    (): TColumn<TCouponBatch>[] => [
      {
        title: 'ID',
        render: (item) => <p className="font-medium">{item?.id}</p>,
      },
      {
        title: 'Status',
        render: (item) => {
          if (!item) return <p>-</p>;
          const status = getBatchStatus(item);

          return (
            <p className={`font-medium ${status.color}`}>{status.label}</p>
          );
        },
      },
      {
        title: 'Usage / Limit',
        render: (item) => {
          if (!item) return <p>-</p>;
          const totalUsage = item.use_count ?? 0;
          const totalUsedCount = item.total_used_count ?? 0;

          return (
            <div className="flex flex-col gap-1">
              <p className="text-sm">
                {totalUsedCount} / {totalUsage}
              </p>
            </div>
          );
        },
      },
      {
        title: 'Discount',
        render: (item) => (
          <p className="font-medium">
            {item?.discount ?? 0}
            {item?.discount_type === 'number' ? ' USD' : '%'}
          </p>
        ),
      },
      {
        title: 'Recurring',
        render: (item) => (
          <p
            className={
              item?.is_apply_recurring ? 'text-green-500' : 'text-gray-400'
            }
          >
            {item?.is_apply_recurring ? 'Yes' : 'No'}
          </p>
        ),
      },
      {
        title: 'Date range',
        render: (item) => {
          const from = item?.start_date ? item.start_date.split('T')[0] : '-';
          const to = item?.end_date ? item.end_date.split('T')[0] : '';

          return (
            <p className="text-sm">
              {from}
              {to ? ` → ${to}` : ''}
            </p>
          );
        },
      },
      {
        title: 'Comment',
        render: (item) => <p className="text-sm">{item?.comment ?? '-'}</p>,
      },
      {
        title: 'Coupons',
        render: (item) => {
          if (!item) return <p className="text-sm text-gray-400">—</p>;
          const codes = item.coupon_codes || [];

          if (codes.length === 0) {
            return <p className="text-sm text-gray-400">No codes</p>;
          }

          const firstCode = codes[0];

          if (codes.length > 1) {
            return (
              <button
                className="text-sm font-medium text-blue hover:underline hover:cursor-pointer"
                type="button"
                onClick={() => handleOpenCouponByBatch(item.id)}
              >
                {codes.length}
              </button>
            );
          }

          return (
            <button
              className="font-mono text-sm font-medium text-blue hover:underline hover:cursor-pointer"
              type="button"
              onClick={() => handleOpenCouponByBatch(item.id)}
            >
              {firstCode.code}
            </button>
          );
        },
      },
      {
        title: '',
        render: (item) => (
          <div className="flex gap-2">
            <Button
              size={'sm'}
              variant={'secondary'}
              onClick={() => handleEdit(item?.id)}
            >
              Edit
            </Button>
            <Button
              size={'sm'}
              variant={'destructive'}
              onClick={() => handleDelete(item?.id)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [handleEdit, handleDelete, getBatchStatus, handleOpenCouponByBatch]
  );

  const handleChangePage = (p: number) => {
    setPagination((prev) => ({ ...prev, currentPage: p }));
    fetchBatches(p, pagination.pageSize, selectedSuggestion);
  };

  const handleChangePageSize = (s: number) => {
    setPagination((prev) => ({ ...prev, pageSize: s, currentPage: 1 }));
    fetchBatches(1, s, selectedSuggestion);
  };

  const applySearchByCode = useCallback(
    (code: string) => {
      const normalized = code.trim();

      setSearchCode(normalized);
      setSelectedSuggestion({
        code: normalized,
        batch_id: 0,
        comment: '',
        label: normalized,
      });

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (normalized) next.set('code', normalized);
        else next.delete('code');

        return next;
      });
    },
    [setSearchParams]
  );

  return (
    <div>
      <div className="flex lg:flex-row flex-col justify-between lg:items-center gap-2 lg:mb-4.5 mb-2">
        <div className={'flex flex-col gap-y-2.5'}>
          <p className={'text-[23px] font-medium text-blue'}>Coupon Batches</p>
          <div className={'h-[2px] w-full bg-blue'} />
        </div>

        <div className="flex items-center gap-4 w-full lg:w-auto">
          <div className="w-full lg:w-[680px] relative">
            <Input
              placeholder="Coupon code"
              textSize="sm"
              value={searchCode}
              onBlur={() => {
                window.setTimeout(() => setSuggestionsOpen(false), 150);
              }}
              onChange={(e) => {
                const val = e.target.value;

                setSearchCode(val);
                setSelectedSuggestion(null);

                // Clear param while typing
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);

                  if (val.trim()) next.set('code', val.trim());
                  else next.delete('code');

                  return next;
                });

                debouncedFetchSuggestions(val);
              }}
              onFocus={() => {
                if (suggestions.length > 0 || moreRowsFound > 0) {
                  setSuggestionsOpen(true);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSuggestionsOpen(false);
                }
                if (e.key === 'Enter') {
                  const normalized = searchCode.trim();

                  if (!normalized) return;

                  applySearchByCode(normalized);
                  setSuggestionsOpen(false);
                }
              }}
            />

            {suggestionsOpen && (
              <div className="absolute z-50 mt-2 w-full rounded-[10px] border border-[#979797] bg-[#0b1b2a] shadow-lg">
                {suggestionsLoading ? (
                  <div className="px-3 py-2 text-sm text-gray-300">
                    Loading…
                  </div>
                ) : suggestions.length === 0 && !moreRowsFound ? (
                  <div className="px-3 py-2 text-sm text-gray-300">
                    No results
                  </div>
                ) : (
                  <ul className="max-h-64 overflow-auto py-1">
                    {suggestions.map((s) => (
                      <li key={`${s.code}-${s.batch_id}`}>
                        <button
                          className="w-full text-left px-3 py-2 text-sm hover:bg-white/10"
                          type="button"
                          onClick={() => {
                            applySearchByCode(s.code);
                            setSuggestionsOpen(false);
                            setSuggestions([]);
                            setMoreRowsFound(0);
                          }}
                          onMouseDown={(ev) => ev.preventDefault()}
                        >
                          {s.label}
                        </button>
                      </li>
                    ))}

                    {moreRowsFound > 0 && (
                      <li>
                        <div className="px-3 py-2 text-sm text-gray-300">
                          ... {moreRowsFound} more rows found ...
                        </div>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>

          {canAccess(
            UserRolePermission.CREATE_DISCOUNT_CODE,
            auth?.user?.permissions
          ) && (
            <Button
              className={'w-fit whitespace-nowrap'}
              size={'sm'}
              variant={'lime'}
              onClick={() => handleEdit()}
            >
              Create coupon batch
            </Button>
          )}
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="p-6">Loading coupon batches...</div>
        ) : (
          <RadixTable columns={columns} data={batches} />
        )}
        <Divider className="w-full mb-6" />
        <AdvancedPagination
          currentPage={pagination.currentPage}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalItems}
          totalPages={pagination.totalPages}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangePageSize}
        />
      </Card>

      <CouponCodesModal
        batchId={modalBatchId}
        open={modalBatchId !== null}
        onOpenChange={(open) => {
          if (!open) setModalBatchId(null);
        }}
      />
    </div>
  );
};

export default CouponsPage;
